<template>
  <controller>
    <page-header title="Detalhes da pessoa">
      <template v-slot:side>
        <router-link class="btn btn-secondary" :to="backRoute">Voltar</router-link>
      </template>
    </page-header>

    <main class="person-page">
      <form-spinner v-if="loading" aria-label="Carregando pessoa" />

      <section v-else-if="loadError" class="state-panel error" role="alert">
        <h2>Não foi possível carregar a pessoa</h2>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-secondary" @click="loadPerson">Tentar novamente</button>
      </section>

      <template v-else-if="detail">
        <pessoa-identity-header
          :person="detail.person"
          :profiles="detail.profiles"
          :has-anomalies="hasAnomalies"
        />

        <section v-if="hasAnomalies" class="anomaly-panel" role="status">
          <strong>Cadastro com pontos para revisão</strong>
          <ul>
            <li v-for="item in anomalyLabels" :key="item">{{ item }}</li>
          </ul>
        </section>

        <nav class="tabs" aria-label="Seções da pessoa">
          <button
            v-for="tab in availableTabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            :aria-current="activeTab === tab.value ? 'page' : undefined"
            @click="selectTab(tab.value)"
          >
            {{ tab.label }}
          </button>
        </nav>

        <section v-if="activeTab === 'summary'" class="content-panel" aria-labelledby="summary-title">
          <div class="section-heading"><div><h2 id="summary-title">Resumo</h2><p>Informações essenciais e situação dos vínculos.</p></div></div>
          <dl class="data-grid">
            <div><dt>Nome</dt><dd>{{ detail.person.nome }}</dd></div>
            <div><dt>Nome no documento</dt><dd>{{ detail.person.nomeDocumento || 'Não informado' }}</dd></div>
            <div><dt>Nome social</dt><dd>{{ detail.person.nomeSocial || 'Não informado' }}</dd></div>
            <div><dt>E-mail</dt><dd>{{ detail.person.email || 'Não informado' }}</dd></div>
            <div><dt>Celular</dt><dd>{{ detail.person.telefonecelular || 'Não informado' }}</dd></div>
            <div><dt>Nascimento</dt><dd>{{ formatDate(detail.person.dataNascimento) }}</dd></div>
            <div><dt>Cadastro</dt><dd>{{ formatDateTime(detail.person.dataCadastro) }}</dd></div>
          </dl>
        </section>

        <section v-if="activeTab === 'personal'" class="content-panel" aria-labelledby="personal-title">
          <div class="section-heading">
            <div><h2 id="personal-title">Dados pessoais</h2><p>Dados da entidade base da pessoa.</p></div>
            <button v-if="canEdit && !editingBasic" type="button" class="btn btn-primary" @click="startBasicEdit">Editar dados</button>
          </div>
          <form v-if="editingBasic" class="edit-form" @submit.prevent="saveBasic">
            <label><span>Nome</span><input v-model.trim="basicForm.nome" required maxlength="80" autocomplete="name"></label>
            <label><span>Nome no documento</span><input v-model.trim="basicForm.nomeDocumento" maxlength="80"></label>
            <label><span>Nome social</span><input v-model.trim="basicForm.nomeSocial" maxlength="80"></label>
            <label><span>Apelido</span><input v-model.trim="basicForm.apelido" maxlength="35"></label>
            <label><span>Data de nascimento</span><input v-model="basicForm.dataNascimento" type="date"></label>
            <label><span>E-mail</span><input v-model.trim="basicForm.email" type="email" maxlength="150" autocomplete="email"></label>
            <label><span>Telefone fixo</span><input v-model.trim="basicForm.telefonefixo" maxlength="80" autocomplete="tel"></label>
            <label><span>Celular</span><input v-model.trim="basicForm.telefonecelular" maxlength="80" autocomplete="tel"></label>
            <label><span>Sexo</span><input v-model.trim="basicForm.sexo" maxlength="1"></label>
            <label class="reason-field"><span>Justificativa da alteração</span><textarea v-model.trim="basicReason" required minlength="5" maxlength="500" rows="3"></textarea></label>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" :disabled="saving" @click="editingBasic = false">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar dados' }}</button>
            </div>
          </form>
          <dl v-else class="data-grid">
            <div><dt>Nome</dt><dd>{{ detail.person.nome }}</dd></div>
            <div><dt>Nome no documento</dt><dd>{{ detail.person.nomeDocumento || 'Não informado' }}</dd></div>
            <div><dt>Nome social</dt><dd>{{ detail.person.nomeSocial || 'Não informado' }}</dd></div>
            <div><dt>Apelido</dt><dd>{{ detail.person.apelido || 'Não informado' }}</dd></div>
            <div><dt>Data de nascimento</dt><dd>{{ formatDate(detail.person.dataNascimento) }}</dd></div>
            <div><dt>E-mail</dt><dd>{{ detail.person.email || 'Não informado' }}</dd></div>
            <div><dt>Telefone fixo</dt><dd>{{ detail.person.telefonefixo || 'Não informado' }}</dd></div>
            <div><dt>Celular</dt><dd>{{ detail.person.telefonecelular || 'Não informado' }}</dd></div>
            <div><dt>Sexo</dt><dd>{{ detail.person.sexo || 'Não informado' }}</dd></div>
          </dl>
        </section>

        <section v-if="activeTab === 'documents'" class="content-panel" aria-labelledby="documents-title">
          <div class="section-heading">
            <div><h2 id="documents-title">Documentos pessoais</h2><p>Todos os registros vinculados à pessoa.</p></div>
            <button v-if="canEdit && recordEditor.type !== 'document'" type="button" class="btn btn-primary" @click="editRecord('document')">Novo documento</button>
          </div>
          <record-editor
            v-if="recordEditor.type === 'document'"
            type="document"
            :record="recordEditor.data"
            :options="options"
            :saving="saving"
            @cancel="closeRecordEditor"
            @save="saveRecord"
          />
          <div v-if="recordDeleteAction && recordDeleteAction.type === 'document'" class="action-panel destructive-panel" role="alert">
            <h3>Excluir documento #{{ recordDeleteAction.record.idDocumento }}</h3>
            <p>Esta ação remove definitivamente o registro. Os dados anteriores permanecerão disponíveis somente na auditoria.</p>
            <label>
              <span>Justificativa da exclusão</span>
              <textarea v-model.trim="recordDeleteAction.reason" required minlength="5" maxlength="500" rows="3"></textarea>
            </label>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" :disabled="saving" @click="recordDeleteAction = null">Cancelar</button>
              <button type="button" class="btn delete-button" :disabled="saving || recordDeleteAction.reason.length < 5" @click="deleteRecord">
                {{ saving ? 'Excluindo...' : 'Excluir definitivamente' }}
              </button>
            </div>
          </div>
          <div v-if="detail.documents.length" class="record-list">
            <article v-for="document in detail.documents" :key="document.idDocumento">
              <div><strong>Documento #{{ document.idDocumento }}</strong><small>CPF {{ formatCpf(document.cpf) }} · RG {{ document.rg || 'não informado' }}</small></div>
              <div v-if="canEdit" class="record-actions">
                <button type="button" class="btn btn-secondary btn-small" @click="editRecord('document', document)">Editar</button>
                <button type="button" class="btn delete-button btn-small" @click="startRecordDelete('document', document)">Excluir</button>
              </div>
            </article>
          </div>
          <p v-else class="empty-message">Nenhum documento pessoal cadastrado.</p>
        </section>

        <section v-if="activeTab === 'addresses'" class="content-panel" aria-labelledby="addresses-title">
          <div class="section-heading">
            <div><h2 id="addresses-title">Endereços e contatos</h2><p>Registros independentes, sem assumir um item principal.</p></div>
            <div class="heading-actions" v-if="canEdit">
              <button type="button" class="btn btn-secondary" @click="editRecord('contact')">Novo contato</button>
              <button type="button" class="btn btn-primary" @click="editRecord('address')">Novo endereço</button>
            </div>
          </div>
          <record-editor
            v-if="recordEditor.type === 'address' || recordEditor.type === 'contact'"
            :type="recordEditor.type"
            :record="recordEditor.data"
            :options="options"
            :saving="saving"
            @cancel="closeRecordEditor"
            @save="saveRecord"
          />
          <div v-if="recordDeleteAction && recordDeleteAction.type === 'address'" class="action-panel destructive-panel" role="alert">
            <h3>Excluir endereço #{{ recordDeleteAction.record.idEndereco }}</h3>
            <p>Esta ação remove definitivamente o endereço. Os dados anteriores permanecerão disponíveis somente na auditoria.</p>
            <label>
              <span>Justificativa da exclusão</span>
              <textarea v-model.trim="recordDeleteAction.reason" required minlength="5" maxlength="500" rows="3"></textarea>
            </label>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" :disabled="saving" @click="recordDeleteAction = null">Cancelar</button>
              <button type="button" class="btn delete-button" :disabled="saving || recordDeleteAction.reason.length < 5" @click="deleteRecord">
                {{ saving ? 'Excluindo...' : 'Excluir definitivamente' }}
              </button>
            </div>
          </div>
          <h3>Endereços</h3>
          <div v-if="detail.addresses.length" class="record-list">
            <article v-for="address in detail.addresses" :key="address.idEndereco">
              <div><strong>{{ address.logradouro || 'Logradouro não informado' }}, {{ address.numero || 's/n' }}</strong><small>{{ address.bairro || 'Sem bairro' }} · {{ address.cidade || 'Sem cidade' }}<template v-if="address.uf">/{{ address.uf }}</template> · {{ address.cep || 'Sem CEP' }}</small></div>
              <div v-if="canEdit" class="record-actions">
                <button type="button" class="btn btn-secondary btn-small" @click="editRecord('address', address)">Editar</button>
                <button type="button" class="btn delete-button btn-small" @click="startRecordDelete('address', address)">Excluir</button>
              </div>
            </article>
          </div>
          <p v-else class="empty-message">Nenhum endereço cadastrado.</p>
          <h3>Contatos</h3>
          <div v-if="detail.contacts.length" class="record-list">
            <article v-for="contact in detail.contacts" :key="contact.idContato">
              <div><strong>{{ contact.tipoContato || 'Contato' }}</strong><small>{{ contact.conteudo }}<template v-if="contact.complemento"> · {{ contact.complemento }}</template></small></div>
              <button v-if="canEdit" type="button" class="btn btn-secondary btn-small" @click="editRecord('contact', contact)">Editar</button>
            </article>
          </div>
          <p v-else class="empty-message">Nenhum contato cadastrado.</p>
        </section>

        <section v-if="activeTab === 'student'" class="content-panel" aria-labelledby="student-title">
          <div class="section-heading"><div><h2 id="student-title">Aluno</h2><p>Vínculos de aluno associados à pessoa.</p></div></div>
          <div v-if="detail.students.length" class="record-list">
            <article v-for="student in detail.students" :key="student.idAluno">
              <form v-if="studentEditor && studentEditor.idAluno === student.idAluno" class="student-edit-form edit-form" @submit.prevent="saveStudent">
                <div class="student-edit-heading">
                  <div><strong>Editar aluno #{{ student.idAluno }}</strong><small>O RM {{ student.rm || 'não informado' }} é gerado pelos sistemas acadêmicos e permanece somente leitura.</small></div>
                </div>
                <label>
                  <span>Unidade</span>
                  <input
                    v-model.trim="studentEditor.unitSearch"
                    type="text"
                    list="student-unit-options"
                    autocomplete="off"
                    required
                    :aria-invalid="studentEditor.unitError ? 'true' : 'false'"
                    :aria-describedby="studentEditor.unitError ? 'student-unit-error' : undefined"
                    @input="resolveStudentUnit(false)"
                    @change="resolveStudentUnit(true)"
                  >
                  <datalist id="student-unit-options">
                    <option v-for="unit in options.student_units" :key="unit.value" :value="studentUnitLabel(unit)" />
                  </datalist>
                  <small v-if="studentEditor.unitError" id="student-unit-error" class="field-error" role="alert">{{ studentEditor.unitError }}</small>
                </label>
                <label><span>RA</span><input v-model.trim="studentEditor.data.ra" maxlength="255"></label>
                <label><span>E-mail acadêmico</span><input v-model.trim="studentEditor.data.emailAcademico" type="email" maxlength="100" autocomplete="email"></label>
                <label><span>Escola anterior</span><input v-model.trim="studentEditor.data.escolaAnterior" maxlength="255"></label>
                <label class="reason-field"><span>Justificativa da alteração</span><textarea v-model.trim="studentEditor.reason" required minlength="5" maxlength="500" rows="3"></textarea></label>
                <div class="form-actions">
                  <button type="button" class="btn btn-secondary" :disabled="saving" @click="studentEditor = null">Cancelar</button>
                  <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar perfil' }}</button>
                </div>
              </form>
              <template v-else>
                <div><strong>Aluno #{{ student.idAluno }}</strong><small>RM {{ student.rm || 'não informado' }} · RA {{ student.ra || 'não informado' }}</small><small>{{ student.unidade || 'Unidade não informada' }} · {{ student.emailAcademico || 'Sem e-mail acadêmico' }}</small><small v-if="student.escolaAnterior">Escola anterior: {{ student.escolaAnterior }}</small></div>
                <button v-if="canEdit" type="button" class="btn btn-secondary btn-small" :disabled="saving || !options.student_units.length" @click="startStudentEdit(student)">{{ options.student_units.length ? 'Editar' : 'Carregando...' }}</button>
              </template>
            </article>
          </div>
          <p v-else class="empty-message">Esta pessoa não possui vínculo de aluno.</p>
        </section>

        <section v-if="activeTab === 'collaborator'" class="content-panel" aria-labelledby="collaborator-title">
          <div class="section-heading"><div><h2 id="collaborator-title">Colaborador</h2><p>Ative ou desative cada vínculo pelo seu identificador.</p></div></div>
          <div v-if="collaboratorAction" class="action-panel">
            <h3>{{ collaboratorAction.target === 'active' ? 'Ativar' : 'Desativar' }} colaborador #{{ collaboratorAction.record.idColaborador }}</h3>
            <label><span>Justificativa</span><textarea v-model.trim="collaboratorAction.reason" required minlength="5" maxlength="500" rows="3"></textarea></label>
            <fieldset><legend>Sincronizar também</legend><label class="check-option"><input v-model="collaboratorAction.integrations" type="checkbox" value="google"> Google</label><label class="check-option"><input v-model="collaboratorAction.integrations" type="checkbox" value="twygo"> Twygo</label></fieldset>
            <div class="form-actions"><button type="button" class="btn btn-secondary" :disabled="saving" @click="collaboratorAction = null">Cancelar</button><button type="button" class="btn btn-primary" :disabled="saving || collaboratorAction.reason.length < 5" @click="saveCollaboratorStatus">Confirmar alteração</button></div>
          </div>
          <div v-if="detail.collaborators.length" class="record-list">
            <article v-for="collaborator in detail.collaborators" :key="collaborator.idColaborador">
              <div><strong>{{ collaborator.funcao || 'Função não informada' }}</strong><small>#{{ collaborator.idColaborador }} · {{ collaborator.unidade || 'Sem unidade' }} · {{ collaborator.situacao || 'Sem situação' }}</small><small>{{ collaborator.departamento || 'Sem departamento' }} / {{ collaborator.setor || 'Sem setor' }}</small></div>
              <button v-if="canManageCollaborator" type="button" :class="['btn', collaborator.idSituacao === 1 ? 'btn-secondary' : 'btn-primary', 'btn-small']" @click="startCollaboratorAction(collaborator)">{{ collaborator.idSituacao === 1 ? 'Desativar' : 'Ativar' }}</button>
            </article>
          </div>
          <p v-else class="empty-message">Esta pessoa não possui vínculo de colaborador.</p>
          <div v-if="integrationResult" :class="['operation-result', integrationResult.overall_status]" role="status">
            <strong>{{ integrationResult.overall_status === 'success' ? 'Alteração concluída' : 'Alteração concluída parcialmente' }}</strong>
            <ul>
              <li v-for="step in integrationResult.steps" :key="step.system">
                {{ step.system }}: {{ integrationStepLabel(step.status) }}
                <button v-if="step.retryable" type="button" class="btn btn-secondary btn-small" :disabled="saving" @click="retryIntegration(step.system)">Repetir</button>
              </li>
            </ul>
          </div>
        </section>

        <section v-if="activeTab === 'users'" class="content-panel" aria-labelledby="users-title">
          <div class="section-heading">
            <div><h2 id="users-title">Usuário</h2><p>Contas vinculadas à pessoa. Credenciais nunca são exibidas.</p></div>
            <button v-if="canManageUsers && !detail.users.length" type="button" class="btn btn-primary" @click="startUserCreate">Criar acesso</button>
          </div>
          <form v-if="userAction" class="action-panel" @submit.prevent="saveUserAction">
            <h3>{{ userAction.title }}</h3>
            <label v-if="['create', 'edit'].includes(userAction.type)">
              <span>E-mail de acesso</span>
              <input v-model.trim="userAction.email" type="email" required maxlength="255" autocomplete="email">
            </label>
            <label><span>Justificativa</span><textarea v-model.trim="userAction.reason" required minlength="5" maxlength="500" rows="3"></textarea></label>
            <div class="form-actions"><button type="button" class="btn btn-secondary" :disabled="saving" @click="userAction = null">Cancelar</button><button type="submit" class="btn btn-primary" :disabled="userActionInvalid">{{ saving ? 'Salvando...' : userAction.type === 'status' ? 'Confirmar' : 'Salvar usuário' }}</button></div>
          </form>
          <div v-if="detail.users.length" class="record-list">
            <article v-for="user in detail.users" :key="user.id">
              <div><strong>{{ user.email || 'E-mail não informado' }}</strong><small>Usuário #{{ user.id }} · {{ userStatusLabel(user.status) }}</small></div>
              <div v-if="canManageUsers" class="record-actions">
                <button type="button" class="btn btn-primary btn-small" @click="startUserEdit(user)">Editar</button>
                <button v-if="['active', 'inactive'].includes(user.status)" type="button" class="btn btn-secondary btn-small" @click="startUserStatus(user)">{{ user.status === 'active' ? 'Desativar' : 'Ativar' }}</button>
              </div>
            </article>
          </div>
          <p v-else class="empty-message">Nenhum usuário cadastrado para esta pessoa.</p>
        </section>

        <section v-if="activeTab === 'audit'" class="content-panel" aria-labelledby="audit-title">
          <div class="section-heading"><div><h2 id="audit-title">Auditoria</h2><p>Alterações realizadas pelo Módulo de Pessoas.</p></div></div>
          <form-spinner v-if="auditLoading" aria-label="Carregando auditoria" />
          <div v-else-if="auditError" class="inline-error" role="alert">{{ auditError }}</div>
          <ol v-else-if="auditItems.length" class="audit-list">
            <li v-for="event in auditItems" :key="event.id">
              <div><strong>{{ auditActionLabel(event.action) }}</strong><small>{{ formatDateTime(event.created_at) }} · por {{ auditActorLabel(event) }} · {{ event.status }}</small></div>
              <p>{{ event.reason }}</p>
            </li>
          </ol>
          <p v-else class="empty-message">Nenhum evento de auditoria encontrado.</p>
        </section>
      </template>
    </main>
  </controller>
</template>

<script>
import PessoaIdentityHeader from '@/components/administrativo/pessoas/PessoaIdentityHeader.vue'
import RecordEditor from '@/components/administrativo/pessoas/RecordEditor.vue'
import PessoasService from '@/services/pessoas.service'
import UserService from '@/services/user.service'

export default {
  name: 'PessoaDetalhe',
  components: { PessoaIdentityHeader, RecordEditor },
  props: { idPessoa: { type: [String, Number], required: true } },
  data () {
    return {
      detail: null,
      loading: true,
      loadError: '',
      saving: false,
      activeTab: this.$route.query.tab || 'summary',
      editingBasic: false,
      basicForm: {},
      basicReason: '',
      recordEditor: { type: null, data: {} },
      recordDeleteAction: null,
      studentEditor: null,
      collaboratorAction: null,
      userAction: null,
      integrationResult: null,
      integrationContext: null,
      auditItems: [],
      auditLoading: false,
      auditError: '',
      options: { issuing_authorities: [], contact_types: [], states: [], student_units: [] }
    }
  },
  computed: {
    canEdit () { return UserService.filterPermissions('editarDadosPessoa') },
    canManageCollaborator () { return UserService.filterPermissions('gerenciarSituacaoColaborador') },
    canManageUsers () { return UserService.filterPermissions('gerenciarAcessoUsuarioPessoa') },
    canViewAudit () { return UserService.filterPermissions('visualizarAuditoriaPessoa') },
    userActionInvalid () {
      if (!this.userAction) return true
      const requiresEmail = ['create', 'edit'].includes(this.userAction.type)
      return this.saving || this.userAction.reason.length < 5 || (requiresEmail && !this.userAction.email)
    },
    availableTabs () {
      const tabs = [
        { value: 'summary', label: 'Resumo' },
        { value: 'personal', label: 'Dados pessoais' },
        { value: 'documents', label: 'Documentos pessoais' },
        { value: 'addresses', label: 'Endereços e contatos' },
        { value: 'student', label: 'Aluno' },
        { value: 'collaborator', label: 'Colaborador' },
        { value: 'users', label: 'Usuário' }
      ]
      if (this.canViewAudit) tabs.push({ value: 'audit', label: 'Auditoria' })
      return tabs
    },
    hasAnomalies () { return Object.values(this.detail?.anomalies || {}).some(Boolean) },
    anomalyLabels () {
      const labels = {
        multiple_documents: 'Há mais de um documento pessoal vinculado.',
        multiple_addresses: 'Há mais de um endereço vinculado.',
        multiple_students: 'Há mais de um vínculo de aluno.',
        multiple_users: 'Há múltiplos usuários vinculados.',
        multiple_active_users: 'Há múltiplos usuários ativos.',
        invalid_user_status: 'Há usuário com status legado ou inválido.',
        collaborator_user_conflict: 'Há colaborador apontando para usuário de outra pessoa.'
      }
      return Object.entries(this.detail?.anomalies || {}).filter(([, active]) => active).map(([key]) => labels[key])
    },
    backRoute () {
      if (!this.$route.query.from) return { name: 'Pessoas' }
      return { name: 'Pessoas', query: { filters: this.$route.query.from } }
    }
  },
  mounted () { this.loadPerson() },
  methods: {
    async loadPerson () {
      this.loading = true
      this.loadError = ''
      try {
        const response = await PessoasService.get(this.idPessoa)
        this.detail = response.data.data
        if (!this.options.contact_types.length) this.loadOptions()
      } catch (error) {
        this.loadError = this.extractError(error)
      } finally {
        this.loading = false
      }
    },
    async loadOptions () {
      try {
        const response = await PessoasService.getOptions()
        this.options = response.data.data
      } catch (error) {
        window.toaster.emit('open', { style: 'error', message: 'Não foi possível carregar as opções dos formulários.', floater: true })
      }
    },
    selectTab (tab) {
      this.activeTab = tab
      this.recordEditor = { type: null, data: {} }
      this.recordDeleteAction = null
      this.studentEditor = null
      this.$router.replace({ query: { ...this.$route.query, tab } })
      if (tab === 'audit' && !this.auditItems.length) this.loadAudit()
    },
    startBasicEdit () {
      this.basicForm = {
        nome: this.detail.person.nome || '',
        nomeDocumento: this.detail.person.nomeDocumento || '',
        nomeSocial: this.detail.person.nomeSocial || '',
        apelido: this.detail.person.apelido || '',
        dataNascimento: this.dateInput(this.detail.person.dataNascimento),
        email: this.detail.person.email || '',
        telefonefixo: this.detail.person.telefonefixo || '',
        telefonecelular: this.detail.person.telefonecelular || '',
        sexo: this.detail.person.sexo || ''
      }
      this.basicReason = ''
      this.editingBasic = true
    },
    async saveBasic () {
      await this.performSave(
        () => PessoasService.updateBasicData(this.idPessoa, { ...this.basicForm, reason: this.basicReason }),
        'Dados pessoais atualizados.',
        () => { this.editingBasic = false }
      )
    },
    editRecord (type, record = null) {
      if (type === 'document' || type === 'address') this.recordDeleteAction = null
      this.recordEditor = { type, data: record ? { ...record } : {} }
    },
    closeRecordEditor () { this.recordEditor = { type: null, data: {} } },
    async saveRecord (payload) {
      const { type, data, reason } = payload
      let request
      if (type === 'document') {
        request = data.idDocumento ? PessoasService.updateDocument(this.idPessoa, data.idDocumento, { ...data, reason }) : PessoasService.createDocument(this.idPessoa, { ...data, reason })
      } else if (type === 'address') {
        request = data.idEndereco ? PessoasService.updateAddress(this.idPessoa, data.idEndereco, { ...data, reason }) : PessoasService.createAddress(this.idPessoa, { ...data, reason })
      } else {
        request = data.idContato ? PessoasService.updateContact(this.idPessoa, data.idContato, { ...data, reason }) : PessoasService.createContact(this.idPessoa, { ...data, reason })
      }
      await this.performSave(() => request, 'Registro salvo com sucesso.', this.closeRecordEditor)
    },
    startRecordDelete (type, record) {
      this.recordEditor = { type: null, data: {} }
      this.recordDeleteAction = { type, record, reason: '' }
    },
    async deleteRecord () {
      const action = this.recordDeleteAction
      if (!action) return

      const isDocument = action.type === 'document'
      const recordId = isDocument ? action.record.idDocumento : action.record.idEndereco
      const label = isDocument ? 'documento' : 'endereço'
      const confirmed = window.confirm(`Excluir definitivamente o ${label} #${recordId}?`)
      if (!confirmed) return

      await this.performSave(
        () => isDocument
          ? PessoasService.deleteDocument(this.idPessoa, recordId, { reason: action.reason })
          : PessoasService.deleteAddress(this.idPessoa, recordId, { reason: action.reason }),
        `${isDocument ? 'Documento' : 'Endereço'} excluído com sucesso.`,
        () => { this.recordDeleteAction = null }
      )
    },
    startStudentEdit (student) {
      const unit = this.options.student_units.find(option => Number(option.value) === Number(student.idUnidade))
      this.studentEditor = {
        idAluno: student.idAluno,
        data: {
          idUnidade: Number(student.idUnidade),
          ra: student.ra || '',
          emailAcademico: student.emailAcademico || '',
          escolaAnterior: student.escolaAnterior || ''
        },
        unitSearch: unit ? this.studentUnitLabel(unit) : '',
        unitError: '',
        reason: ''
      }
    },
    resolveStudentUnit (showError = false) {
      if (!this.studentEditor) return
      const unit = this.options.student_units.find(option => this.studentUnitLabel(option) === this.studentEditor.unitSearch)
      this.studentEditor.data.idUnidade = unit ? Number(unit.value) : null
      this.studentEditor.unitError = unit || !showError ? '' : 'Selecione uma unidade válida da lista.'
    },
    studentUnitLabel (unit) {
      const acronym = unit.sigla ? ` (${unit.sigla})` : ''
      const status = Number(unit.active) === 1 ? '' : ' · Inativa'
      return `${unit.label}${acronym} · #${unit.value}${status}`
    },
    async saveStudent () {
      this.resolveStudentUnit(true)
      if (!this.studentEditor || this.studentEditor.unitError) return
      const editor = this.studentEditor
      await this.performSave(
        () => PessoasService.updateStudent(this.idPessoa, editor.idAluno, { ...editor.data, reason: editor.reason }),
        'Perfil de aluno atualizado.',
        () => { this.studentEditor = null }
      )
    },
    startCollaboratorAction (record) {
      this.collaboratorAction = {
        record,
        target: record.idSituacao === 1 ? 'inactive' : 'active',
        reason: '',
        integrations: ['google', 'twygo']
      }
      this.integrationResult = null
    },
    async saveCollaboratorStatus () {
      const action = this.collaboratorAction
      const confirmed = window.confirm(`${action.target === 'active' ? 'Ativar' : 'Desativar'} o colaborador #${action.record.idColaborador}?`)
      if (!confirmed) return
      await this.performSave(async () => {
        const response = await PessoasService.changeCollaboratorStatus(this.idPessoa, action.record.idColaborador, {
          status: action.target,
          expected_status: this.collaboratorStatus(action.record.idSituacao),
          reason: action.reason,
          integrations: action.integrations
        })
        this.integrationResult = response.data.data
        this.integrationContext = {
          collaboratorId: action.record.idColaborador,
          targetStatus: action.target,
          reason: action.reason
        }
        return response
      }, 'Situação do colaborador processada.', () => { this.collaboratorAction = null })
    },
    async retryIntegration (integration) {
      if (!this.integrationContext || !this.integrationResult?.operation_id) return
      const confirmed = window.confirm(`Repetir a sincronização com ${integration}?`)
      if (!confirmed) return
      await this.performSave(async () => {
        const response = await PessoasService.retryCollaboratorIntegration(
          this.idPessoa,
          this.integrationContext.collaboratorId,
          {
            integration,
            target_status: this.integrationContext.targetStatus,
            reason: this.integrationContext.reason,
            operation_id: this.integrationResult.operation_id
          }
        )
        const retriedStep = response.data.data.steps[0]
        this.integrationResult.steps = this.integrationResult.steps.map(step => step.system === integration ? retriedStep : step)
        this.integrationResult.overall_status = this.integrationResult.steps.some(step => step.status === 'failed') ? 'partial' : 'success'
        return response
      }, 'Integração reprocessada.')
    },
    startUserCreate () {
      this.userAction = { type: 'create', title: 'Criar acesso', email: this.detail.person.email || '', reason: '' }
    },
    startUserEdit (user) {
      this.userAction = { type: 'edit', title: `Editar usuário #${user.id}`, email: user.email || '', reason: '', user }
    },
    startUserStatus (user) {
      this.userAction = { type: 'status', title: user.status === 'active' ? 'Desativar usuário' : 'Ativar usuário', reason: '', user, target: user.status === 'active' ? 'inactive' : 'active' }
    },
    async saveUserAction () {
      const action = this.userAction
      let request
      if (action.type === 'create') {
        request = () => PessoasService.createUser(this.idPessoa, { expected_user_ids: [], email: action.email, reason: action.reason })
      } else if (action.type === 'edit') {
        request = () => PessoasService.updateUser(this.idPessoa, action.user.id, { email: action.email, expected_email: action.user.email, reason: action.reason })
      } else {
        request = () => PessoasService.changeUserStatus(this.idPessoa, action.user.id, { status: action.target, expected_is_active: action.user.isActive, reason: action.reason })
      }
      await this.performSave(request, action.type === 'create' ? 'Acesso criado com sucesso.' : 'Usuário atualizado com sucesso.', () => { this.userAction = null })
    },
    async loadAudit () {
      this.auditLoading = true
      this.auditError = ''
      try {
        const response = await PessoasService.getAudit(this.idPessoa)
        this.auditItems = response.data.data.data || []
      } catch (error) {
        this.auditError = this.extractError(error)
      } finally {
        this.auditLoading = false
      }
    },
    async performSave (request, successMessage, afterSuccess) {
      this.saving = true
      try {
        await request()
        window.toaster.emit('open', { style: 'success', message: successMessage, floater: true })
        if (afterSuccess) afterSuccess()
        await this.loadPerson()
        if (this.activeTab === 'audit') await this.loadAudit()
      } catch (error) {
        window.toaster.emit('open', { style: 'error', message: this.extractError(error), floater: true })
      } finally {
        this.saving = false
      }
    },
    extractError (error) { return error.response?.data?.message || 'Não foi possível concluir a operação.' },
    collaboratorStatus (status) { return Number(status) === 1 ? 'active' : Number(status) === 2 ? 'inactive' : 'unknown' },
    userStatusLabel (status) { return { active: 'Ativo', inactive: 'Inativo', legacy_unknown: 'Status não definido', invalid: 'Status inválido' }[status] || status },
    auditActionLabel (action) { return String(action || '').replace('person.', '').replaceAll('_', ' ') },
    auditActorLabel (event) { return event.actor_name || `Usuário #${event.actor_user_id}` },
    integrationStepLabel (status) { return { success: 'sucesso', failed: 'falha', already_completed: 'já concluída' }[status] || status },
    dateInput (value) { return value ? String(value).slice(0, 10) : '' },
    formatDate (value) { return value ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(String(value).slice(0, 10) + 'T00:00:00Z')) : 'Não informado' },
    formatDateTime (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(String(value).replace(' ', 'T'))) : 'Não informado' },
    formatCpf (value) {
      const digits = String(value || '').replace(/\D/g, '')
      return digits.length === 11 ? digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : (value || 'não informado')
    }
  }
}
</script>

<style lang="scss" scoped>
.person-page { max-width: 1320px; margin: 0 auto; padding: $mg_mid; color: $color-dev; }
.state-panel, .content-panel, .anomaly-panel { margin-bottom: $mg_mid; padding: $hmg_mid; background: $color-secondary; border: $border-component; border-radius: $border-radius; }
.state-panel { text-align: center; }
.state-panel.error, .inline-error { border-color: $color-error !important; }
.anomaly-panel { margin-top: $mg_small; border-color: $color-warning !important; }
.anomaly-panel ul { margin: $mg_mini 0 0; padding-left: $hmg_mid; }
.tabs { display: flex; gap: $hmg_mini; margin: $mg_small 0; padding-bottom: $hmg_mini; overflow-x: auto; }
.tabs button { min-height: 42px; padding: 0 $mg_small; border: $border-component; border-radius: $border-radius-xlarge; background: $color-secondary; color: $color-text; font-weight: 700; white-space: nowrap; }
.tabs button.active { background: $color-primary; border-color: $color-primary !important; color: $color-secondary; }
.tabs button:focus-visible, input:focus-visible, textarea:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; margin-bottom: $hmg_mid; }
.section-heading h2 { margin: 0 0 $hmg_mini; font-size: 20px; }
.section-heading p { margin: 0; color: $color-text; }
.heading-actions, .form-actions { display: flex; flex-wrap: wrap; gap: $mg_mini; justify-content: flex-end; }
.data-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: $mg_small; margin: 0; }
.data-grid div { padding-bottom: $mg_small; border-bottom: $border-component; }
.data-grid dt { color: $color-text; font-size: $font-size-small; }
.data-grid dd { margin: $hmg_mini 0 0; font-weight: 600; overflow-wrap: anywhere; }
.edit-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $mg_small; }
label { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
input, textarea { width: 100%; min-height: 44px; padding: $mg_mini $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
.reason-field, .form-actions { grid-column: 1 / -1; }
.record-list { display: grid; gap: $mg_mini; }
.record-list article { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; padding: $mg_small; border: $border-component; border-radius: $border-radius-mid; }
.record-list strong, .record-list small { display: block; }
.record-list small { margin-top: $hmg_mini; color: $color-text; overflow-wrap: anywhere; }
.btn-small { min-height: 36px; padding: $hmg_mini $mg_mini; flex: 0 0 auto; }
.empty-message { padding: $mg_small; background: $color-bg; border-radius: $border-radius-mid; color: $color-text; text-align: center; }
h3 { margin: $hmg_mid 0 $mg_mini; font-size: 16px; }
.action-panel { margin-bottom: $mg_small; padding: $hmg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-bg; }
.action-panel h3 { margin-top: 0; }
.destructive-panel { border-color: $color-error !important; }
.destructive-panel p { color: $color-text; }
.record-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: $mg_mini; }
.student-edit-form { width: 100%; }
.student-edit-heading { grid-column: 1 / -1; }
.student-edit-heading small { margin-top: $hmg_mini; color: $color-text; }
.field-error { color: $color-error; }
.delete-button { border: 1px solid $color-error; background: $color-secondary; color: $color-error; font-weight: 700; }
.delete-button:hover, .delete-button:focus-visible { background: $color-error; color: $color-secondary; }
.action-panel fieldset { display: flex; gap: $mg_small; margin: $mg_small 0; border: 0; padding: 0; }
.check-option { display: inline-flex; align-items: center; flex-direction: row; }
.check-option input { width: 20px; min-height: 20px; }
.operation-result { margin-top: $mg_small; padding: $mg_small; border: $border-component; border-radius: $border-radius-mid; }
.operation-result.partial { border-color: $color-warning !important; }
.audit-list { display: grid; gap: $mg_mini; margin: 0; padding: 0; list-style: none; }
.audit-list li { padding: $mg_small; border-bottom: $border-component; }
.audit-list small { display: block; margin-top: $hmg_mini; color: $color-text; }
.audit-list p { margin: $mg_mini 0 0; }
.inline-error { padding: $mg_small; border: $border-component; border-radius: $border-radius-mid; }
@media (max-width: 900px) {
  .person-page { padding: $hmg_mid; }
  .data-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .person-page { padding: $mg_small; }
  .section-heading, .record-list article { align-items: stretch; flex-direction: column; }
  .heading-actions .btn, .section-heading > .btn, .record-list .btn { width: 100%; min-height: 44px; }
  .data-grid, .edit-form { grid-template-columns: 1fr; }
  .reason-field, .form-actions, .student-edit-heading { grid-column: auto; }
  .form-actions { flex-direction: column-reverse; }
  .form-actions .btn { width: 100%; }
}
</style>
